import React, { useState, useEffect } from "react";
import GuestModalHeader from "./GuestModal/GuestModalHeader";
import GuestNameFields from "./GuestModal/GuestNameFields";
import EventSelector from "./GuestModal/EventSelector";
import TagManager from "./GuestModal/TagManager";

interface Event {
  id: number;
  name: string;
}

interface Guest {
  id: number;
  firstName: string;
  lastName?: string;
  phoneNumber?: string;
  message?: string;
  invitations: Array<{
    eventId: number;
    event: {
      id: number;
      name: string;
    };
  }>;
  rsvps: Array<{
    eventId: number;
    event: {
      id: number;
      name: string;
    };
    attending: boolean;
  }>;
  tags?: Array<{
    tag: {
      id: number;
      name: string;
    };
  }>;
}

interface GuestModalProps {
  guest?: Guest | null;
  isOpen?: boolean;
  events: Event[];
  onClose: () => void;
  onSave: (
    id: number | null,
    firstName: string,
    lastName: string,
    phoneNumber: string,
    eventIds: number[],
  ) => Promise<number>;
}

const GuestModal: React.FC<GuestModalProps> = ({
  guest,
  isOpen,
  events,
  onClose,
  onSave,
}) => {
  const [firstName, setFirstName] = useState(guest?.firstName || "");
  const [lastName, setLastName] = useState(guest?.lastName || "");
  const [phoneNumber, setPhoneNumber] = useState(guest?.phoneNumber || "");
  const [selectedEventIds, setSelectedEventIds] = useState<Set<number>>(
    new Set(),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const isEditMode = !!guest;

  useEffect(() => {
    if (guest) {
      setFirstName(guest.firstName);
      setLastName(guest.lastName || "");
      setPhoneNumber(guest.phoneNumber || "");
      setSelectedEventIds(new Set(guest.invitations.map((inv) => inv.eventId)));
      setSelectedTags(guest.tags?.map(t => t.tag.name) || []);
    }
  }, [guest]);

  if (!isEditMode && !isOpen) return null;

  const handleToggleEvent = (eventId: number) => {
    const newSet = new Set(selectedEventIds);
    if (newSet.has(eventId)) {
      newSet.delete(eventId);
    } else {
      newSet.add(eventId);
    }
    setSelectedEventIds(newSet);
  };

  const handleSave = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError("");

    if (!firstName.trim()) {
      setError("Le prénom est requis");
      return;
    }

    if (selectedEventIds.size === 0) {
      setError("Sélectionnez au moins un événement");
      return;
    }

    setSaving(true);
    try {
      // Save guest name and events
      const guestId = await onSave(
        guest?.id || null,
        firstName.trim(),
        lastName.trim(),
        phoneNumber.trim(),
        Array.from(selectedEventIds),
      );

      // Update tags
      if (guestId && selectedTags.length > 0) {
        await fetch(`/api/admin/guests/${guestId}/tags`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tagNames: selectedTags }),
        });
      }

      // Reset form if adding
      if (!isEditMode) {
        setFirstName("");
        setLastName("");
        setPhoneNumber("");
        setSelectedEventIds(new Set());
        setSelectedTags([]);
      }

      onClose();
    } catch (err: any) {
      setError(err.message || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isEditMode && guest) {
      setFirstName(guest.firstName);
      setLastName(guest.lastName || "");
      setPhoneNumber(guest.phoneNumber || "");
      setSelectedEventIds(new Set(guest.invitations.map((inv) => inv.eventId)));
      setSelectedTags(guest.tags?.map(t => t.tag.name) || []);
    } else {
      setFirstName("");
      setLastName("");
      setPhoneNumber("");
      setSelectedEventIds(new Set());
      setSelectedTags([]);
    }
    setError("");
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className={`modal-content ${!isEditMode ? 'add-guest-modal' : ''}`} onClick={(e) => e.stopPropagation()}>
        <GuestModalHeader
          isEditMode={isEditMode}
          firstName={firstName}
          lastName={lastName}
          phoneNumber={phoneNumber}
          onFirstNameChange={setFirstName}
          onLastNameChange={setLastName}
          onPhoneNumberChange={setPhoneNumber}
          onClose={handleCancel}
        />

        <form onSubmit={handleSave}>
          <div className="modal-body">
            {!isEditMode && (
              <GuestNameFields
                firstName={firstName}
                lastName={lastName}
                onFirstNameChange={setFirstName}
                onLastNameChange={setLastName}
                disabled={saving}
              />
            )}

            <EventSelector
              events={events}
              selectedEventIds={selectedEventIds}
              onToggleEvent={handleToggleEvent}
              disabled={saving}
            />

            <TagManager
              selectedTags={selectedTags}
              onTagsChange={setSelectedTags}
              disabled={saving}
            />

            {error && (
              <div className="error-banner">
                {error}
              </div>
            )}

            <div className="modal-actions">
              <button
                type="button"
                onClick={handleCancel}
                className="modern-btn secondary"
                disabled={saving}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="modern-btn primary"
                disabled={saving || !firstName.trim()}
              >
                {saving ? (isEditMode ? "Enregistrement..." : "Ajout...") : (isEditMode ? "Enregistrer" : "Ajouter")}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GuestModal;
